import React, { useEffect, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Activity,
  Bell,
  Eye,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Globe,
  Clock,
  ExternalLink,
  AlertTriangle,
  Settings,
  Plus,
  Target,
  Database
} from 'lucide-react';
import { MarketStatus } from '../components/MarketStatus';
import { useAppStore, useFuturesData, useMarketSentiment, useNewsSentiment } from '../store';
import { useDocumentTitle } from '../hooks';
import { cn, formatNumber, getChangeColor } from '../utils';
import { marketDataService } from '../services/marketData';
import { newsService } from '../services/newsService';
import FuturesCarousel from '../components/FuturesCarousel';
import AssetsList from '../components/AssetsList';
import EconomicIndicators from '../components/EconomicIndicators';
import DataQualityMonitor from '../components/DataQualityMonitor';

const Dashboard: React.FC = () => {
  useDocumentTitle('Dashboard');
  const { 
    marketData, 
    updateMarketData, 
    news, 
    watchlist,
    updateFuturesData,
    updateMarketSentiment,
    setNews,
    updateNewsSentiment,
    addToWatchlist,
    addAlert
  } = useAppStore();
  
  const futuresData = useFuturesData();
  const marketSentiment = useMarketSentiment();
  const newsSentiment = useNewsSentiment();
  
  const [realTimeIntervals, setRealTimeIntervals] = useState<{
    market?: () => void;
    news?: () => void;
  }>({});
  const [showDataDebug, setShowDataDebug] = useState(false);

  // Initialize real-time data
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load initial futures data
        const futures = await marketDataService.getFuturesData();
        updateFuturesData(futures);
        
        // Load initial market sentiment
        const sentiment = await marketDataService.getMarketSentiment();
        updateMarketSentiment(sentiment);
        
        // Load initial news
        const newsData = await newsService.fetchNews();
        setNews(newsData);
        
        // Load news sentiment
        const newsSent = await newsService.getNewsSentiment();
        updateNewsSentiment(newsSent);
        
        // Mock market data for other components
        const mockMarketData = [
          {
            symbol: 'BTC/USD',
            name: 'Bitcoin',
            price: 43250.50,
            change: 1250.30,
            changePercent: 2.98,
            volume: 28500000000,
            marketCap: 850000000000,
            high24h: 44100.00,
            low24h: 41800.00,
            lastUpdated: new Date().toISOString()
          },
          {
            symbol: 'ETH/USD',
            name: 'Ethereum',
            price: 2650.75,
            change: -85.25,
            changePercent: -3.11,
            volume: 15200000000,
            marketCap: 320000000000,
            high24h: 2750.00,
            low24h: 2580.00,
            lastUpdated: new Date().toISOString()
          },
          {
            symbol: 'AAPL',
            name: 'Apple Inc.',
            price: 185.92,
            change: 2.45,
            changePercent: 1.34,
            volume: 45000000,
            marketCap: 2900000000000,
            high24h: 187.50,
            low24h: 183.20,
            lastUpdated: new Date().toISOString()
          },
          {
            symbol: 'TSLA',
            name: 'Tesla Inc.',
            price: 248.50,
            change: -12.30,
            changePercent: -4.72,
            volume: 85000000,
            marketCap: 790000000000,
            high24h: 265.80,
            low24h: 245.00,
            lastUpdated: new Date().toISOString()
          }
        ];
        
        updateMarketData(mockMarketData);
        
      } catch (error) {
        console.error('Error initializing dashboard data:', error);
      }
    };
    
    initializeData();
  }, [updateMarketData, updateFuturesData, updateMarketSentiment, setNews, updateNewsSentiment]);
  
  // Set up real-time updates
  useEffect(() => {
    const marketInterval = marketDataService.startRealTimeUpdates(async (futures) => {
      updateFuturesData(futures);
      const sentiment = await marketDataService.getMarketSentiment();
      updateMarketSentiment(sentiment);
    }); // Update every 30 seconds - REAL TIME DATA
    
    const newsInterval = newsService.startNewsUpdates(async (newsData) => {
      setNews(newsData);
      const newsSent = await newsService.getNewsSentiment();
      updateNewsSentiment(newsSent);
    }, 300000); // Update every 5 minutes
    
    setRealTimeIntervals({ market: marketInterval, news: newsInterval });
    
    return () => {
      if (marketInterval) marketInterval();
      if (newsInterval) newsInterval();
    };
  }, [updateFuturesData, updateMarketSentiment, setNews, updateNewsSentiment]);

  // Calculate dominant sentiment with clear algorithm
  const calculateDominantSentiment = (sentiment: any) => {
    if (!sentiment) return { dominant: 'Loading...', confidence: 0, breakdown: 'Loading...' };
    
    const { positiveCount, negativeCount, neutralCount, totalCount } = sentiment;
    const positivePercent = Math.round((positiveCount / totalCount) * 100);
    const negativePercent = Math.round((negativeCount / totalCount) * 100);
    const neutralPercent = Math.round((neutralCount / totalCount) * 100);
    
    let dominant = 'Neutral';
    let confidence = 0;
    
    if (positiveCount > negativeCount && positiveCount > neutralCount) {
      dominant = 'Positive';
      confidence = positivePercent;
    } else if (negativeCount > positiveCount && negativeCount > neutralCount) {
      dominant = 'Negative';
      confidence = negativePercent;
    } else {
      dominant = 'Neutral';
      confidence = neutralPercent;
    }
    
    const breakdown = `${positiveCount}P • ${neutralCount}N • ${negativeCount}Neg`;
    
    return { dominant, confidence, breakdown, positivePercent, negativePercent, neutralPercent };
  };
  
  const sentimentData = calculateDominantSentiment(newsSentiment);

  const stats = [
    {
      title: 'News Sentiment',
      value: sentimentData.dominant,
      change: `${sentimentData.confidence}% Confidence`,
      changePercent: sentimentData.breakdown,
      icon: Activity,
      color: sentimentData.dominant === 'Positive' ? 'from-green-400 to-emerald-600' : 
             sentimentData.dominant === 'Negative' ? 'from-red-400 to-rose-600' : 
             'from-gray-400 to-gray-600',
      isPositive: sentimentData.dominant === 'Positive',
      detailedInfo: newsSentiment ? {
        positive: `${newsSentiment.positiveCount} Positive (${sentimentData.positivePercent}%)`,
        neutral: `${newsSentiment.neutralCount} Neutral (${sentimentData.neutralPercent}%)`,
        negative: `${newsSentiment.negativeCount} Negative (${sentimentData.negativePercent}%)`,
        total: `Total: ${newsSentiment.totalCount} news`
      } : null
    },
    {
      title: 'Market Sentiment',
      value: marketSentiment.length > 0 ? `${marketSentiment.filter(m => m.sentiment === 'Bullish').length}/${marketSentiment.length} Bullish` : 'Loading...',
      change: marketSentiment.length > 0 ? `${marketSentiment.filter(m => m.changePercent > 0).length} Rising` : '0',
      changePercent: 'Major Futures',
      icon: TrendingUp,
      color: 'from-blue-400 to-cyan-600',
      isPositive: marketSentiment.filter(m => m.sentiment === 'Bullish').length > marketSentiment.filter(m => m.sentiment === 'Bearish').length
    },
    {
      title: 'Active Alerts',
      value: '11',
      change: '+4',
      changePercent: 'Today',
      icon: Bell,
      color: 'from-purple-400 to-pink-600',
      isPositive: true,
      hasContent: true,
      content: [
        { type: 'Price Alert', symbol: 'GOLD', condition: '> $2,100', status: 'Active' },
        { type: 'Change Alert', symbol: 'SPX', condition: '> 5,000', status: 'Triggered' },
        { type: 'RSI Alert', symbol: 'NDX', condition: '> 70', status: 'Active' },
        { type: 'Price Alert', symbol: 'DAX', condition: '< 17,000', status: 'Active' },
        { type: 'Technical Alert', symbol: 'DXY', condition: 'RSI < 30', status: 'Triggered' }
      ]
    },
    {
      title: 'Live News',
      value: '20',
      change: '+10',
      changePercent: 'Last Hour',
      icon: Globe,
      color: 'from-orange-400 to-red-600',
      isPositive: true,
      hasContent: true,
      content: news.slice(0, 3)
    }
  ];

  const topMovers = marketData.slice(0, 4);

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="glass-card p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl" />
        <div className="relative z-10">
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome back, <span className="gradient-text">Trader</span>
          </h1>
          <p className="text-gray-300 text-lg">
            Stay updated with the latest market alerts and financial news
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const isNewsSentiment = stat.title === 'News Sentiment';
          const isMarketSentiment = stat.title === 'Market Sentiment';
          
          // Market Sentiment card con carosello
          if (isMarketSentiment) {
            return (
              <div key={index} className="lg:col-span-1 h-full">
                <FuturesCarousel futuresData={futuresData} />
              </div>
            );
          }
          
          const isActiveAlerts = stat.title === 'Active Alerts';
          const isLiveNews = stat.title === 'Live News';
          
          return (
            <div key={index} className="glass-card p-6 hover:scale-105 transition-transform duration-300 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className={cn(
                  "w-12 h-12 rounded-xl bg-gradient-to-r flex items-center justify-center",
                  stat.color
                )}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-right">
                  <div className={cn(
                    "text-sm font-medium",
                    stat.isPositive ? "text-green-400" : "text-red-400"
                  )}>
                    {stat.changePercent}
                  </div>
                </div>
              </div>
              <div className="flex-1 flex flex-col">
                <h3 className="text-gray-400 text-sm font-medium mb-1">{stat.title}</h3>
                <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className={cn(
                  "text-sm font-medium",
                  stat.isPositive ? "text-green-400" : "text-red-400"
                )}>
                  {stat.change}
                </p>
                
                {/* Detailed breakdown for News Sentiment */}
                {isNewsSentiment && stat.detailedInfo && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-green-400">✓ Positive:</span>
                        <span className="text-white">{stat.detailedInfo.positive}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">○ Neutral:</span>
                        <span className="text-white">{stat.detailedInfo.neutral}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-400">✗ Negative:</span>
                        <span className="text-white">{stat.detailedInfo.negative}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-white/5">
                        <span className="text-blue-400 font-medium">{stat.detailedInfo.total}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Active Alerts Content */}
                {isActiveAlerts && stat.content && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="space-y-2">
                      {stat.content.map((alert: any, alertIndex: number) => (
                        <div key={alertIndex} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                          <div>
                            <p className="text-xs font-medium text-white">{alert.type}</p>
                            <p className="text-xs text-gray-400">{alert.symbol} {alert.condition}</p>
                          </div>
                          <span className={cn(
                            "text-xs px-2 py-1 rounded",
                            alert.status === 'Active' ? 'text-green-400 bg-green-500/20' : 'text-orange-400 bg-orange-500/20'
                          )}>
                            {alert.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Live News Content */}
                {isLiveNews && stat.content && stat.content.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="space-y-2">
                      {stat.content.map((newsItem: any, newsIndex: number) => (
                        <div key={newsIndex} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                          <p className="text-xs font-medium text-white line-clamp-2 mb-1">
                            {newsItem.title}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className={cn(
                              "text-xs px-2 py-1 rounded",
                              newsItem.category === 'breaking' ? 'text-red-400 bg-red-500/20' :
                              newsItem.category === 'markets' ? 'text-blue-400 bg-blue-500/20' :
                              'text-gray-400 bg-gray-500/20'
                            )}>
                              {newsItem.category}
                            </span>
                            <span className="text-xs text-gray-400">
                              {Math.floor((Date.now() - new Date(newsItem.publishedAt).getTime()) / (1000 * 60))}m ago
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Economic Indicators Section */}
      <div className="mb-8">
        <EconomicIndicators className="w-full" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Market Overview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lista completa degli asset */}
          <div>
            <AssetsList futuresData={futuresData} />
          </div>

          {/* Quick Actions */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Quick Actions
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { 
                  label: 'Add to Watchlist', 
                  icon: Eye, 
                  color: 'from-blue-400 to-cyan-600',
                  action: () => {
                    // Add current top gainer to watchlist
                    const topGainer = futuresData.filter(item => item.changePercent > 0)[0];
                    if (topGainer) {
                      addToWatchlist({
                        symbol: topGainer.symbol,
                        name: topGainer.name,
                        price: topGainer.price,
                        change: topGainer.change,
                        changePercent: topGainer.changePercent
                      });
                    }
                  }
                },
                { 
                  label: 'Set Price Alert', 
                  icon: Bell, 
                  color: 'from-orange-400 to-red-600',
                  action: () => {
                    // Create a price alert for EUR/USD
                    const eurUsd = futuresData.find(item => item.symbol === 'EUR/USD');
                    if (eurUsd) {
                      addAlert({
                        type: 'price',
                        title: 'EUR/USD Price Alert',
                        message: `Alert when EUR/USD ${eurUsd.changePercent > 0 ? 'rises above' : 'falls below'} ${(eurUsd.price + (eurUsd.changePercent > 0 ? 0.01 : -0.01)).toFixed(4)}`,
                        symbol: 'EUR/USD',
                        threshold: eurUsd.price + (eurUsd.changePercent > 0 ? 0.01 : -0.01),
                        condition: eurUsd.changePercent > 0 ? 'above' : 'below',
                        isActive: true
                      });
                    }
                  }
                },
                { 
                  label: 'Market Analysis', 
                  icon: BarChart3, 
                  color: 'from-purple-400 to-pink-600',
                  action: () => {
                    // Scroll to market volatility section
                    const volatilitySection = document.querySelector('[data-section="volatility"]');
                    volatilitySection?.scrollIntoView({ behavior: 'smooth' });
                  }
                },
                { 
                  label: 'News Alerts', 
                  icon: Globe, 
                  color: 'from-green-400 to-emerald-600',
                  action: () => {
                    // Create a news alert for breaking news
                    addAlert({
                      type: 'news',
                      title: 'Breaking News Alert',
                      message: 'Get notified of breaking financial news',
                      isActive: true
                    });
                  }
                },
                { 
                  label: 'Data Quality', 
                  icon: Database, 
                  color: 'from-cyan-400 to-cyan-600',
                  action: () => {
                    setShowDataDebug(!showDataDebug);
                  }
                }
              ].map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200 hover:scale-105 group"
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-lg bg-gradient-to-r flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform",
                      action.color
                    )}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                      {action.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Top Gainers */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Top Gainers
            </h2>
            
            <div className="space-y-3">
              {futuresData.filter(item => item.changePercent > 0).slice(0, 3).map((item, index) => {
                const Icon = ArrowUpRight;
                return (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-white font-bold text-xs">
                        {item.symbol.substring(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">{item.name}</p>
                        <p className="text-xs text-gray-400">
                          {item.symbol.includes('JPY') ? item.price.toFixed(2) : 
                           item.symbol.includes('DXY') ? item.price.toFixed(2) :
                           item.price < 10 ? item.price.toFixed(4) : formatNumber.currency(item.price)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-green-400 font-medium text-sm">
                        <Icon className="w-3 h-3" />
                        +{formatNumber.percentage(item.changePercent)}
                      </div>
                      <p className="text-xs text-green-400">
                        +{item.change.toFixed(4)}
                      </p>
                    </div>
                  </div>
                );
              })}
              {futuresData.filter(item => item.changePercent > 0).length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-400 text-sm">No gainers at the moment</p>
                </div>
              )}
            </div>
          </div>

          {/* Market Volatility */}
          <div className="glass-card p-6" data-section="volatility">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Market Volatility
            </h2>
            
            <div className="space-y-3">
              {futuresData.slice(0, 6).map((item, index) => {
                const isPositive = item.changePercent > 0;
                const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
                const sentimentColor = item.sentiment === 'Bullish' ? 'text-green-400' : 
                                     item.sentiment === 'Bearish' ? 'text-red-400' : 'text-yellow-400';
                const volatility = Math.abs(item.changePercent);
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs",
                        isPositive ? "bg-green-500/20" : "bg-red-500/20"
                      )}>
                        {item.symbol.substring(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">{item.name}</p>
                        <p className={cn("text-xs font-medium", sentimentColor)}>
                          {item.sentiment} • {volatility.toFixed(2)}% Vol
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-white font-medium text-sm">
                        {item.symbol.includes('JPY') ? item.price.toFixed(2) : 
                         item.symbol.includes('DXY') ? item.price.toFixed(2) :
                         item.price < 10 ? item.price.toFixed(4) : formatNumber.currency(item.price)}
                      </p>
                      <div className={cn(
                        "flex items-center gap-1 text-xs font-medium",
                        getChangeColor(item.change)
                      )}>
                        <Icon className="w-3 h-3" />
                        {formatNumber.percentage(Math.abs(item.changePercent))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Live Financial News */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-400" />
              Live Financial News
            </h2>
            
            <div className="space-y-4">
              {news.slice(0, 5).map((newsItem, index) => {
                const timeAgo = Math.floor((Date.now() - new Date(newsItem.publishedAt).getTime()) / (1000 * 60));
                const sentimentColor = newsItem.sentiment === 'positive' ? 'text-green-400' : 
                                     newsItem.sentiment === 'negative' ? 'text-red-400' : 'text-gray-400';
                
                const handleNewsClick = () => {
                  if (newsItem.url && newsItem.url.startsWith('http')) {
                    window.open(newsItem.url, '_blank', 'noopener,noreferrer');
                  }
                };
                
                return (
                  <div key={newsItem.id} className="cursor-pointer hover:bg-white/5 p-3 rounded-lg transition-colors group" onClick={handleNewsClick}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-white text-sm font-medium mb-1 line-clamp-2 group-hover:text-blue-300 transition-colors flex-1 pr-2">
                        {newsItem.title}
                      </h3>
                      <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-blue-400 transition-colors flex-shrink-0 mt-1" />
                    </div>
                    
                    <p className="text-xs text-gray-400 mb-2 line-clamp-2">
                      {newsItem.summary}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded",
                          newsItem.category === 'breaking' ? 'text-red-400 bg-red-500/20' :
                          newsItem.category === 'markets' ? 'text-blue-400 bg-blue-500/20' :
                          newsItem.category === 'crypto' ? 'text-purple-400 bg-purple-500/20' :
                          'text-gray-400 bg-gray-500/20'
                        )}>
                          {newsItem.category}
                        </span>
                        <span className={cn("text-xs font-medium", sentimentColor)}>
                          {newsItem.sentiment}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{newsItem.source}</span>
                        <span>•</span>
                        <span>{timeAgo < 60 ? `${timeAgo}m` : `${Math.floor(timeAgo / 60)}h`} ago</span>
                      </div>
                    </div>
                    
                    {/* Tags would be displayed here if available */}
                  </div>
                );
              })}
              
              {news.length === 0 && (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Loading latest financial news...</p>
                </div>
              )}
            </div>
          </div>

          {/* Market Status */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Market Status
            </h2>
            
            <MarketStatus />
          </div>
        </div>
      </div>
      
      {/* Data Quality Monitor */}
      <DataQualityMonitor 
        isVisible={showDataDebug}
        onToggle={() => setShowDataDebug(!showDataDebug)}
      />
    </div>
  );
};

export default Dashboard;